/* eslint-disable jira/filename-case */
export const GLOBAL_APP_MENU_ID = {
	GLOBAL_APP_GOALS: 'jira.sidebar.globalApp.goals',
	GLOBAL_APP_ASSETS: 'jira.sidebar.globalApp.assets',
	// Global App Shortcuts for Platform Component
	GLOBAL_APP_CUSTOMER_SERVICE: 'jira.sidebar.globalApp.customerService',
	GLOBAL_APP_CONFLUENCE: 'jira.sidebar.globalApp.confluence',
	GLOBAL_APP_JIRA_SERVICE_MANAGEMENT: 'jira.sidebar.globalApp.jiraServiceManagement',
	GLOBAL_APP_LOOM: 'jira.sidebar.globalApp.loom',
	GLOBAL_APP_PROJECTS: 'jira.sidebar.globalApp.projects',
	GLOBAL_APP_TEAMS: 'jira.sidebar.globalApp.teams',
	GLOBAL_APP_OPSGENIE: 'jira.sidebar.globalApp.opsgenie',
} as const;

// Maps global app shortcuts menu ids to aps keys. Any new shortcuts added to jira
// will need to be added here.
export const GLOBAL_APP_MENU_ID_TO_APS_KEY: Record<string, string> = {
	[GLOBAL_APP_MENU_ID.GLOBAL_APP_CONFLUENCE]: 'CONFLUENCE',
	[GLOBAL_APP_MENU_ID.GLOBAL_APP_JIRA_SERVICE_MANAGEMENT]: 'JIRA_SERVICE_DESK',
	[GLOBAL_APP_MENU_ID.GLOBAL_APP_LOOM]: 'LOOM',
	[GLOBAL_APP_MENU_ID.GLOBAL_APP_GOALS]: 'GOALS',
	[GLOBAL_APP_MENU_ID.GLOBAL_APP_PROJECTS]: 'PROJECTS',
	[GLOBAL_APP_MENU_ID.GLOBAL_APP_TEAMS]: 'TEAMS',
	[GLOBAL_APP_MENU_ID.GLOBAL_APP_OPSGENIE]: 'OPSGENIE',
	[GLOBAL_APP_MENU_ID.GLOBAL_APP_CUSTOMER_SERVICE]: 'JIRA_CUSTOMER_SERVICE',
};

// These IDs are used with the user-customisable navigation service.
export const CONFIGURABLE_MENU_ID: {
	readonly GLOBAL_APP_GOALS: 'jira.sidebar.globalApp.goals';
	readonly GLOBAL_APP_ASSETS: 'jira.sidebar.globalApp.assets';
	// Global App Shortcuts for Platform Component
	readonly GLOBAL_APP_CONFLUENCE: 'jira.sidebar.globalApp.confluence';
	readonly GLOBAL_APP_JIRA_SERVICE_MANAGEMENT: 'jira.sidebar.globalApp.jiraServiceManagement';
	readonly GLOBAL_APP_LOOM: 'jira.sidebar.globalApp.loom';
	readonly GLOBAL_APP_PROJECTS: 'jira.sidebar.globalApp.projects';
	readonly GLOBAL_APP_TEAMS: 'jira.sidebar.globalApp.teams';
	readonly GLOBAL_APP_OPSGENIE: 'jira.sidebar.globalApp.opsgenie';
	readonly GLOBAL_APP_CUSTOMER_SERVICE: 'jira.sidebar.globalApp.customerService';
	readonly APPS: 'jira.sidebar.apps';
	readonly ASSETS: 'jira.sidebar.assets';
	readonly DASHBOARDS: 'jira.sidebar.dashboards';
	readonly FILTERS: 'jira.sidebar.filters';
	readonly GOALS: 'jira.sidebar.goals';
	readonly OPERATIONS: 'jira.sidebar.operations';
	readonly PLANS: 'jira.sidebar.plans';
	readonly PROJECTS: 'jira.sidebar.projects';
	readonly RECENT: 'jira.sidebar.recent';
	readonly STARRED: 'jira.sidebar.starred';
	readonly TEAMS: 'jira.sidebar.teams';
	readonly ROADMAPS: 'jira.sidebar.roadmaps';
	readonly CUSTOMER_SERVICE: 'jira.sidebar.customerService';
	readonly CUSTOMER_SERVICE_ANALYTICS: 'jira.sidebar.customerServiceAnalytics';
	readonly CUSTOMER_CONVERSATIONS_ANALYTICS: 'jira.sidebar.customerConversationsAnalytics';
	readonly CUSTOMERS: 'jira.sidebar.customers';
	readonly ITAM: 'jira.sidebar.itam';
	readonly ITOM: 'jira.sidebar.itom';
	readonly HELP_CENTER_MANAGEMENT: 'jira.sidebar.helpCenterManagement';
	readonly DISCOVERY: 'jira.sidebar.discovery';
} = {
	APPS: 'jira.sidebar.apps',
	ASSETS: 'jira.sidebar.assets',
	DASHBOARDS: 'jira.sidebar.dashboards',
	FILTERS: 'jira.sidebar.filters',
	GOALS: 'jira.sidebar.goals',
	OPERATIONS: 'jira.sidebar.operations',
	PLANS: 'jira.sidebar.plans',
	PROJECTS: 'jira.sidebar.projects',
	RECENT: 'jira.sidebar.recent',
	STARRED: 'jira.sidebar.starred',
	TEAMS: 'jira.sidebar.teams',
	ROADMAPS: 'jira.sidebar.roadmaps',
	CUSTOMER_SERVICE: 'jira.sidebar.customerService',
	CUSTOMER_SERVICE_ANALYTICS: 'jira.sidebar.customerServiceAnalytics',
	CUSTOMER_CONVERSATIONS_ANALYTICS: 'jira.sidebar.customerConversationsAnalytics',
	CUSTOMERS: 'jira.sidebar.customers',
	ITAM: 'jira.sidebar.itam',
	ITOM: 'jira.sidebar.itom',
	HELP_CENTER_MANAGEMENT: 'jira.sidebar.helpCenterManagement',
	DISCOVERY: 'jira.sidebar.discovery',
	...GLOBAL_APP_MENU_ID,
} as const;

export type ConfigurableMenuId = (typeof CONFIGURABLE_MENU_ID)[keyof typeof CONFIGURABLE_MENU_ID];

// The entity types are used to uniquely identify an entity instance.
// The entity's id should be in the URL
export const ENTITY_ID = {
	ALARM_QUEUE: (id: string): string => `alrm_${id}`,
	APP: (id: string): string => `app_${id}`,
	BOARD: (id: string): string => `boar_${id}`,
	DASHBOARD: (id: string): string => `dash_${id}`,
	FILTER: (id: string): string => `filt_${id}`,
	FORM: (id: string): string => `form_${id}`,
	OVERVIEW: (id: string): string => `over_${id}`,
	PLAN: (id: string): string => `plan_${id}`,
	PROJECT: (id: string): string => `proj_${id}`,
	PROJECT_CALENDAR: (projectKey: string): string => `proj-${projectKey}-cal`,
	PROJECT_CHANGE_CALENDAR: (projectKey: string): string => `proj-${projectKey}-chgcal`,
	PROJECT_ESCALATIONS: (projectKey: string): string => `proj-${projectKey}-escl`,
	PROJECT_DIRECTORY: (projectKey: string): string => `proj-${projectKey}-dir`,
	PROJECT_FILTERS: (projectKey: string): string => `proj-${projectKey}-filters`,
	PROJECT_WORKFORCE_OPTIMIZATION: (projectKey: string): string =>
		`proj-${projectKey}-workforce-optimization`,
	PROJECT_WORKFORCE_OPTIMIZATION_SCHEDULES: (projectKey: string): string =>
		`proj-${projectKey}-wfo-schedules`,
	PROJECT_WORKFORCE_OPTIMIZATION_CAPACITY: (projectKey: string): string =>
		`proj-${projectKey}-wfo-capacity`,
	PROJECT_WORKFORCE_OPTIMIZATION_AVAILABILITY: (projectKey: string): string =>
		`proj-${projectKey}-wfo-availability`,
	PROJECT_WORKFORCE_OPTIMIZATION_SKILLS: (projectKey: string): string =>
		`proj-${projectKey}-wfo-skills`,
	PROJECT_ISSUE_FILTER: (projectKey: string, filterId: string): string =>
		`proj-${projectKey}-issf-${filterId}`,
	PROJECT_JSM_GETTING_STARTED: (projectKey: string): string => `proj-${projectKey}-jsm-gtst`,
	PROJECT_JCS_GET_STARTED: (projectKey: string): string => `proj-${projectKey}-jcs-gtst`,
	PROJECT_KNOWLEDGE_BASE: (projectKey: string): string => `proj-${projectKey}-kb`,
	PROJECT_OPERATIONS: (projectKey: string): string => `proj-${projectKey}-ops`,
	PROJECT_NOTIFICATION_LOGS: (projectKey: string): string => `proj-${projectKey}-nlog`,
	PROJECT_ONCALL_SCHEDULE: (projectKey: string): string => `proj-${projectKey}-oncs`,
	PROJECT_PRACTICE_QUEUE: (projectKey: string, practice: string): string =>
		`proj-${projectKey}-pract-${practice}`,
	PROJECT_QUEUES: (projectKey: string): string => `proj-${projectKey}-queues`,
	PROJECT_CHANNELS: (projectKey: string): string => `proj-${projectKey}-channels`,
	PROJECT_REPORTS: (projectKey: string): string => `proj-${projectKey}-rprt`,
	PROJECT_SERVICES: (projectKey: string): string => `proj-${projectKey}-srvc`,
	PROJECT_SUMMARY: (projectKey: string): string => `proj-${projectKey}-summary`,
	PROJECT_PROACTIVE_SUPPORT: (projectKey: string): string => `proj-${projectKey}-proactive-support`,
	SURVEYS: (projectKey: string): string => `proj-${projectKey}-surveys`,
	XLA_INSIGHTS: (projectKey: string): string => `proj-${projectKey}-xla-insights`,

	PROJECT_VIEWS: (projectKey: string): string => `proj-${projectKey}-views`,
	PROJECT_LIVE_CHAT: (projectKey: string): string => `proj-${projectKey}-live-chat`,
	ROADMAP: (id: string): string => `roadmap_${id}`,
	QUEUE: (id: string): string => `queu_${id}`,
	SUBSPACE: (id: string): string => `subspace_${id}`,
	ISSUE: (id: string): string => `issue_${id}`,
	ARCHIVED_WORK_ITEMS: (id: string): string => `archived_work_items_${id}`,
	PROJECT_ROVO_AGENT: (projectKey: string): string => `proj-${projectKey}-rovo-agent`,
	PROJECT_JOURNEYS: (projectKey: string): string => `proj-${projectKey}-journeys`,
	PROJECT_LIVE_CONVERSATIONS: (projectKey: string): string =>
		`proj-${projectKey}-live-conversations`,
	CUSTOMER_EXPERIENCE: (projectKey: string): string => `customer-experiences-${projectKey}`,
	CUSTOMER_EXPERIENCES_CHANNELS: (projectKey: string): string =>
		`customer-experiences-channels-${projectKey}`,
	CUSTOMER_EXPERIENCE_MENU_ITEM: (helpCenterId: string, itemName: string): string =>
		`customer-experiences-${helpCenterId}-${itemName}`,
	CHANGE: (id: string): string => `chng_${id}`,
	INCIDENT: (id: string): string => `incd_${id}`,
} as const; // We MUST use the same IDs for the same components, otherwise we run into trouble (especially with peeking / more-menu)

export const MENU_ID_APPS: 'jira.sidebar.apps' = CONFIGURABLE_MENU_ID.APPS;
export const MENU_ID_APP_MARKETPLACE_DISCOVER = 'app_mrkt';
export const MENU_ID_ASSETS: 'jira.sidebar.assets' = CONFIGURABLE_MENU_ID.ASSETS;
export const MENU_ID_ASSETS_SCHEMAS = 'asst_schm';
export const MENU_ID_ASSETS_FOR_YOU = 'asst_for_you';
export const MENU_ID_ASSETS_REPORTS = 'asst_rprt';
export const MENU_ID_BOARDS_VIEW_ALL = 'board-view-all';
export const MENU_ID_DASHBOARDS: 'jira.sidebar.dashboards' = CONFIGURABLE_MENU_ID.DASHBOARDS;
export const MENU_ID_DASHBOARDS_VIEW_ALL = 'dash-view';
export const MENU_ID_DASHBOARDS_VIEW_ALL_STARRED = 'dash-view-starred';
export const MENU_ID_FILTERS: 'jira.sidebar.filters' = CONFIGURABLE_MENU_ID.FILTERS;
export const MENU_ID_FILTERS_SEARCH = 'filt-search';
export const MENU_ID_FILTERS_VIEW_ALL = 'filt-view-all';
export const MENU_ID_FILTERS_VIEW_ALL_STARRED = 'filt-view-starred';
export const MENU_ID_GOALS: 'jira.sidebar.goals' = CONFIGURABLE_MENU_ID.GOALS;
export const MENU_ID_OPERATIONS: 'jira.sidebar.operations' = CONFIGURABLE_MENU_ID.OPERATIONS;
export const MENU_ID_OPS_ALERTS = 'ops-alrt';
export const MENU_ID_OPS_OVERVIEW = 'ops-over';
export const MENU_ID_OPS_REPORTS = 'ops-rprt';
export const MENU_ID_OPS_REPORTS_NEW = 'ops-rprt-new';
export const MENU_ID_OPS_ON_CALL = 'ops-oncl';
export const MENU_ID_OPS_SERVICES = 'ops-srvc';
export const MENU_ID_OPS_CHANGES = 'ops-chng';
export const MENU_ID_OPS_INCIDENTS = 'ops-incd';
export const MENU_ID_PLANS: 'jira.sidebar.plans' = CONFIGURABLE_MENU_ID.PLANS;
export const MENU_ID_PLANS_VIEW_ALL = 'plan-view-all';
export const MENU_ID_PROJECT_APPS = 'proj-app';
export const MENU_ID_PROJECTS: 'jira.sidebar.projects' = CONFIGURABLE_MENU_ID.PROJECTS;
export const MENU_ID_PROJECTS_VIEW_ALL = 'proj-view-all';
export const MENU_ID_PROJECTS_VIEW_ALL_STARRED = 'proj-view-starred';
export const MENU_ID_RECENT: 'jira.sidebar.recent' = CONFIGURABLE_MENU_ID.RECENT;
export const MENU_ID_ROADMAPS: 'jira.sidebar.roadmaps' = CONFIGURABLE_MENU_ID.ROADMAPS;
export const MENU_ID_ROADMAPS_VIEW_ALL = 'roadmaps-view-all';
export const MENU_ID_STARRED: 'jira.sidebar.starred' = CONFIGURABLE_MENU_ID.STARRED;
export const MENU_ID_TEAMS: 'jira.sidebar.teams' = CONFIGURABLE_MENU_ID.TEAMS;
export const MENU_ID_CUSTOMER_SERVICE: 'jira.sidebar.customerService' =
	CONFIGURABLE_MENU_ID.CUSTOMER_SERVICE;
export const MENU_ID_CUSTOMER_SERVICE_ANALYTICS: 'jira.sidebar.customerServiceAnalytics' =
	CONFIGURABLE_MENU_ID.CUSTOMER_SERVICE_ANALYTICS;
export const MENU_ID_CUSTOMER_CONVERSATIONS_ANALYTICS: 'jira.sidebar.customerConversationsAnalytics' =
	CONFIGURABLE_MENU_ID.CUSTOMER_CONVERSATIONS_ANALYTICS;
export const MENU_ID_CUSTOMER_EXPERIENCES_VIEW_ALL = 'customer-experiences-view-all';
export const MENU_ID_CUSTOMERS: 'jira.sidebar.customers' = CONFIGURABLE_MENU_ID.CUSTOMERS;
export const MENU_ID_ITAM: 'jira.sidebar.itam' = CONFIGURABLE_MENU_ID.ITAM;
export const MENU_ID_ITAM_HAM = 'itam-ham';
export const MENU_ID_ITAM_SAM = 'itam-sam';
export const MENU_ID_ITOM: 'jira.sidebar.itom' = CONFIGURABLE_MENU_ID.ITOM;
export const MENU_ID_FOR_YOU = 'for-you';
export const MENU_ID_CSM_WORK_ITEMS = 'csm-work-items';
export const MENU_ID_GLOBAL_APP_ASSETS: 'jira.sidebar.globalApp.assets' =
	CONFIGURABLE_MENU_ID.GLOBAL_APP_ASSETS;
export const MENU_ID_GLOBAL_APP_GOALS: 'jira.sidebar.globalApp.goals' =
	CONFIGURABLE_MENU_ID.GLOBAL_APP_GOALS;
export const MENU_ID_COMMUNICATIONS = 'comm';
export const MENU_ID_COMMUNICATIONS_STAKEHOLDERS = 'comm-stkhldr';
export const MENU_ID_COMMUNICATIONS_STAKEHOLDER_GROUPS = 'comm-stkhldr-grp';
export const MENU_ID_OPS_STATUS_PAGES = 'ops-status-pages';
// Global App Shortcuts for Platform Component
export const MENU_ID_GLOBAL_APP_CONFLUENCE: 'jira.sidebar.globalApp.confluence' =
	CONFIGURABLE_MENU_ID.GLOBAL_APP_CONFLUENCE;
export const MENU_ID_GLOBAL_APP_JIRA_SERVICE_MANAGEMENT: 'jira.sidebar.globalApp.jiraServiceManagement' =
	CONFIGURABLE_MENU_ID.GLOBAL_APP_JIRA_SERVICE_MANAGEMENT;
export const MENU_ID_GLOBAL_APP_LOOM: 'jira.sidebar.globalApp.loom' =
	CONFIGURABLE_MENU_ID.GLOBAL_APP_LOOM;
export const MENU_ID_GLOBAL_APP_PROJECTS: 'jira.sidebar.globalApp.projects' =
	CONFIGURABLE_MENU_ID.GLOBAL_APP_PROJECTS;
export const MENU_ID_GLOBAL_APP_TEAMS: 'jira.sidebar.globalApp.teams' =
	CONFIGURABLE_MENU_ID.GLOBAL_APP_TEAMS;
export const MENU_ID_GLOBAL_APP_OPSGENIE: 'jira.sidebar.globalApp.opsgenie' =
	CONFIGURABLE_MENU_ID.GLOBAL_APP_OPSGENIE;
export const MENU_ID_GLOBAL_APP_CUSTOMER_SERVICE: 'jira.sidebar.globalApp.customerService' =
	CONFIGURABLE_MENU_ID.GLOBAL_APP_CUSTOMER_SERVICE;

// JSM Project Settings Menu IDs
// Menu Groups
export const MENU_ID_SD_SETTINGS_ACCESS = 'sd-sett-acc';
export const MENU_ID_SD_SETTINGS_REQUEST_MANAGEMENT = 'sd-sett-req-man';
export const MENU_ID_SD_SETTINGS_QUEUES = 'sd-sett-queues';
export const MENU_ID_SD_SETTINGS_REQUEST_MANAGEMENT_REQUEST_TYPES = 'sd-sett-req-typs';
export const MENU_ID_SD_SETTINGS_AUTOMATION = 'sd-sett-aut';
export const MENU_ID_SD_SETTINGS_CHANNELS_AND_SELF_SERVICE = 'sd-sett-ch-slf-srv';
export const MENU_ID_SD_SETTINGS_NOTIFICATIONS = 'sd-sett-not';
export const MENU_ID_SD_SETTINGS_APPS = 'sd-sett-apps';
export const MENU_ID_SD_SETTINGS_OPERATIONS = 'sd-sett-ops';
export const MENU_ID_SD_SETTINGS_WORKFORCE_OPTIMIZATION = 'sd-sett-wfo';

export const MENU_ID_HELP_CENTER_MANAGEMENT: 'jira.sidebar.helpCenterManagement' =
	CONFIGURABLE_MENU_ID.HELP_CENTER_MANAGEMENT;
export const MENU_ID_DISCOVERY: 'jira.sidebar.discovery' = CONFIGURABLE_MENU_ID.DISCOVERY;
